import { useState } from "react";
import { useTranslation } from "react-i18next";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";

import { useRefreshedPhoto } from "./hooks";

interface ImagePresignedProps {
  photoSrc: string;
  photoId: string;
  onClick?: (
    url: string,
    event?: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
  className?: string;
  srcExpire?: string;
  isThumbnail?: boolean;
}

const Image = ({
  photoSrc,
  photoId,
  srcExpire,
  onClick = () => {},
  className = "",
  isThumbnail = false,
}: ImagePresignedProps) => {
  const { t } = useTranslation();
  const refreshedPhoto = useRefreshedPhoto(photoId, srcExpire);
  const [failedSrc, setFailedSrc] = useState<string>();

  const src =
    (isThumbnail ? refreshedPhoto?.srcThumbnail : refreshedPhoto?.srcUrl) ??
    photoSrc;

  if (!photoSrc) {
    return null;
  }

  // The server signs a thumbnail URL for every image and video, even one stored
  // without a thumbnail, so a tile can point at nothing.
  if (isThumbnail && failedSrc === src) {
    return (
      <div
        role="img"
        aria-label={t("common:images.load_failed")}
        className={`w-full h-full flex items-center justify-center text-gray-500 ${className}`}
        onClick={(e) => onClick(photoSrc, e)}
      >
        <ImageOutlinedIcon fontSize="small" />
      </div>
    );
  }

  return (
    <img
      key={photoId}
      className={`w-full h-full object-contain ${className}`}
      src={src}
      alt={photoId}
      title={photoId}
      onClick={(e) => onClick(photoSrc ?? "", e)}
      onError={() => setFailedSrc(src)}
      crossOrigin="anonymous"
      loading="lazy"
    />
  );
};

export default Image;
